import LoadError from "@/components/LoadError";
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Check, CheckCircle2 } from "lucide-react";
import { Card, Button, Input, Pill, Spinner, useToast } from "@/components/earnos/kit";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { colors, money } from "@/lib/theme";

const PLAN_TINT = { start: colors.onSurfaceSecondary, pro: colors.brandSecondary, business: colors.brandTertiary };

function FeeRow({ label, value, bold }) {
  return (
    <div className="flex items-center justify-between">
      <span className={bold ? "text-base font-extrabold text-ink" : "text-base text-ink-2"}>{label}</span>
      <span className={bold ? "text-xl font-extrabold text-ok" : "text-base font-bold text-ink"}>{value}</span>
    </div>
  );
}

export default function Billing() {
  const navigate = useNavigate();
  const toast = useToast();
  const { user, refresh } = useAuth();
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState(null);
  const [usage, setUsage] = useState(null);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [busy, setBusy] = useState(null);
  const [gross, setGross] = useState("1000");
  const [quote, setQuote] = useState(null);
  const [config, setConfig] = useState(null);
  const [connect, setConnect] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setLoadError("");
    try {
      const [p, s, u, t, c, cn] = await Promise.all([
        apiFetch("/plans"), apiFetch("/billing/subscription"), apiFetch("/ai/usage"),
        apiFetch("/billing/transactions"), apiFetch("/billing/config"), apiFetch("/connect/status"),
      ]);
      setPlans(p.plans || []); setSub(s); setUsage(u); setTxns(t.transactions || []); setConfig(c); setConnect(cn);
    } catch (e) {
      setLoadError(e.message || "Service unavailable. Please retry.");
      if (e?.status !== 401) toast.show(e.message || "Could not load billing", "error");
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const rawPlan = sub?.plan || user?.plan || "start";
  const currentPlan = rawPlan === "free" ? "start" : rawPlan;
  const quoteRequest = React.useRef(0);

  const subscribe = async (planId) => {
    setBusy(planId);
    try {
      const res = await apiFetch("/billing/subscribe", { method: "POST", body: { plan: planId } });
      if (res.free) { await refresh(); await load(); toast.show("You're on the START plan", "success"); }
      else if (res.url) window.location.href = res.url;
    } catch (e) { toast.show(e.message || "Could not start checkout", "error"); }
    finally { setBusy(null); }
  };

  const cancel = async () => {
    setBusy("cancel");
    try { const res = await apiFetch("/billing/cancel", { method: "POST" }); toast.show(res.note || "Subscription cancelled", "success"); await load(); }
    catch (e) { toast.show(e.message || "Could not cancel", "error"); }
    finally { setBusy(null); }
  };

  const runQuote = async () => {
    const amount = parseFloat(gross);
    if (!amount || amount <= 0) { toast.show("Enter a valid amount", "error"); return; }
    const request = ++quoteRequest.current;
    setQuote(null);
    try { const result = await apiFetch("/fees/quote", { method: "POST", body: { amount, plan: currentPlan } }); if (request === quoteRequest.current) setQuote(result); }
    catch (e) { if (request === quoteRequest.current) toast.show(e.message || "Could not calculate", "error"); }
  };

  const setupPayouts = async () => {
    setBusy("payout");
    try { const res = await apiFetch("/connect/account", { method: "POST" }); if (res.url) window.location.href = res.url; }
    catch (e) { toast.show(e.message || "Could not start payout setup", "error"); }
    finally { setBusy(null); }
  };

  if (loading) return <Spinner className="py-24" />;

  const pct = usage?.usage_percent || 0;
  const barColor = pct >= 100 ? colors.error : pct >= 80 ? colors.warning : colors.success;

  if (loadError && !loading) return <LoadError title="Billing" message={loadError} onRetry={load} />;

  return (
    <div className="animate-fade-in max-w-3xl">
      <div className="flex items-center gap-3 mb-5">
        <button onClick={() => navigate(-1)} aria-label="Go back" data-testid="billing-back" className="text-ink lg:hidden"><ChevronLeft size={26} /></button>
        <h1 className="text-2xl font-extrabold tracking-tight text-ink">Membership &amp; Billing</h1>
      </div>

      <Card className="mb-5">
        <div className="flex items-center justify-between">
          <p className="earnos-kicker">AI usage this month</p>
          <Pill label={(currentPlan || "free").toUpperCase()} color={PLAN_TINT[currentPlan] || colors.brandSecondary} />
        </div>
        <div className="h-2.5 rounded-full bg-surface-3 mt-3 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: barColor }} /></div>
        <p className="text-sm text-ink-2 mt-2">{usage?.month?.requests || 0} / {usage?.limits?.requests || 0} requests · {pct}% used</p>
        {usage?.hard_cap_reached ? <p className="text-sm text-danger mt-1">You&apos;ve reached this month&apos;s AI limit. Upgrade for more capacity.</p> : usage?.soft_cap_reached ? <p className="text-sm text-warn mt-1">You&apos;re nearing your monthly AI limit.</p> : null}
        {usage?.kill_switch ? <p className="text-sm text-danger mt-1">AI is temporarily disabled by an administrator.</p> : null}
      </Card>

      {config?.connect_enabled ? (
        <Card className="mb-5">
          <div className="flex items-center justify-between">
            <p className="earnos-kicker">Getting paid</p>
            {connect?.payouts_enabled ? <Pill label="ACTIVE" color={colors.success} /> : <Pill label="SETUP NEEDED" color={colors.warning} />}
          </div>
          {connect?.payouts_enabled ? (
            <p className="text-sm text-ink-2 mt-2">Your payout account is active. Net amounts from EARNOS-facilitated payments are deposited to you automatically.</p>
          ) : (
            <>
              <p className="text-sm text-ink-2 mt-2">Connect a payout account so buyers can pay you through EARNOS. You keep the net; EARNOS keeps only its disclosed fee.</p>
              <Button title={connect?.connected ? "Finish payout setup" : "Set up payouts"} small loading={busy === "payout"} onClick={setupPayouts} className="mt-3" testID="setup-payouts" />
              {connect?.requirements_due?.length ? <p className="text-sm text-warn mt-2">Stripe still needs: {connect.requirements_due.slice(0, 3).join(", ")}</p> : null}
            </>
          )}
        </Card>
      ) : null}

      <h3 className="text-lg font-extrabold text-ink mb-3">Plans</h3>
      <div className="grid sm:grid-cols-3 gap-3">
        {plans.map((p) => {
          const isCurrent = p.id === currentPlan;
          return (
            <Card key={p.id} className={isCurrent ? "border-brand-light" : ""}>
              <div className="flex items-center justify-between">
                <p className="text-xl font-extrabold text-ink">{p.name}</p>
                <p className="text-lg font-extrabold text-brand-light">{p.price > 0 ? `$${p.price}${p.recurring_billing ? "/mo" : " / 30 days"}` : "Free"}</p>
              </div>
              <p className="text-sm text-ink-2 mt-0.5">{p.fee_percent}% fee on EARNOS-facilitated transactions</p>
              {p.ai_description ? <p className="text-sm text-ink-3 mt-3">{p.ai_description}</p> : null}
              {p.ai_model ? <p className="text-xs text-ink-2 mt-1">Assistant: {p.ai_model} · {p.ai_reasoning} reasoning. Engine plans: {p.ai_engine_reasoning || p.ai_reasoning} reasoning.</p> : null}
              {p.price > 0 ? <p className="text-xs text-ink-2 mt-2">{p.recurring_billing ? "Renews monthly. Cancel in Billing." : "One payment for 30 days of access. Does not renew automatically."}</p> : null}
              <div className="space-y-1 mt-3 mb-4">
                {[...new Set((Array.isArray(p.features) ? p.features : []).filter(f => typeof f === "string" && f.trim().length >= 3).map(f => f.trim()))].slice(0, 5).map((f) => <div key={f} className="flex items-center gap-1.5"><Check size={14} className="text-ok shrink-0" /><span className="text-sm text-ink-3">{f}</span></div>)}
              </div>
              {isCurrent ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5"><CheckCircle2 size={16} className="text-ok" /><span className="text-base font-bold text-ok">Current plan</span></div>
                  {p.price > 0 && sub?.subscription?.stripe_subscription_id && sub?.subscription?.status === "active" && !sub?.subscription?.cancel_at_period_end ? <Button title="Cancel subscription" variant="ghost" small loading={busy === "cancel"} onClick={cancel} full testID={`cancel-${p.id}`} /> : null}
                  {sub?.subscription?.cancel_at_period_end ? <p className="text-sm text-ink-2">Cancels at period end, then reverts to START.</p> : null}
                </div>
              ) : (
                <Button title={p.price > 0 ? `Upgrade to ${p.name}` : "Switch to START"} small full loading={busy === p.id} onClick={() => subscribe(p.id)} testID={`subscribe-${p.id}`} />
              )}
            </Card>
          );
        })}
      </div>

      <h3 className="text-lg font-extrabold text-ink mb-3 mt-8">EARNOS facilitation fee</h3>
      <Card className="mb-5">
        <p className="text-sm text-ink-2 mb-3 leading-relaxed">See exactly what a buyer pays and what you receive on a transaction EARNOS facilitates. Fees never apply to unrelated external income.</p>
        <Input label="Transaction amount (USD)" type="number" value={gross} onChange={(e) => { quoteRequest.current += 1; setQuote(null); setGross(e.target.value); }} testID="fee-amount" />
        <Button title="Calculate breakdown" variant="secondary" small onClick={runQuote} testID="fee-calc" />
        {quote ? (
          <div className="mt-4 space-y-2 rounded-xl bg-surface border border-line p-4">
            <FeeRow label="Gross amount" value={money(quote.transaction_amount, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} />
            <FeeRow label={`EARNOS fee (${quote.earnos_fee_percent}%)`} value={`- ${money(quote.earnos_fee, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
            <FeeRow label="Payment processing" value={`- ${money(quote.payment_processing_fee, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
            <div className="h-px bg-line my-1" />
            <FeeRow label="You receive" value={money(quote.provider_receives, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} bold />
            <p className="text-sm text-ink-2 mt-2 leading-relaxed">Estimate only. Calculating a quote does not charge anyone. Final processing fees depend on the payment.</p>
          </div>
        ) : null}
      </Card>

      <h3 className="text-lg font-extrabold text-ink mb-3">Payment history</h3>
      {txns.length === 0 ? (
        <p className="text-base text-ink-2">No payments yet.</p>
      ) : (
        <div className="space-y-2">
          {txns.map((t) => (
            <div key={t.session_id} className="flex items-center gap-3 rounded-xl border border-line bg-surface-2 p-4">
              <div className="flex-1 min-w-0">
                <p className="text-base font-bold text-ink">{t.kind === "subscription" ? `${(t.plan || "").toUpperCase()} plan` : "Facilitation fee"}</p>
                <p className="text-sm text-ink-2">{money(t.amount || 0, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} · {t.created_at?.slice(0, 10)}</p>
              </div>
              <Pill label={(t.payment_status || "initiated").toUpperCase()} color={t.payment_status === "paid" ? colors.success : t.payment_status === "refunded" ? colors.info : t.payment_status === "failed" ? colors.error : colors.warning} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
